import React, { useState } from "react";
import { useI18n } from "@/lib/i18n";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, CheckCircle2 } from "lucide-react";

const FIELD_LABELS = {
  fr: { task_type: "Type de travail", parcelle_name: "Parcelle", date: "Date", cost: "Coût (FCFA)", assignee: "Assigné à", equipment: "Matériel", pond_ref: "Bassin", feed_type: "Aliment", quantity_kg: "Quantité (kg)", name: "Nom", category: "Catégorie", phone: "Téléphone", city: "Ville", email: "Email", supplier_name: "Fournisseur", item: "Article", quantity: "Quantité", unit_cost: "Prix unitaire (FCFA)", target_name: "Bassin", health_type: "Type", issue: "Problème", product: "Produit", dosage: "Dosage", customer_name: "Client", order_ref: "Commande", avg_weight_g: "Poids moyen (g)", oxygen_mg_l: "Oxygène (mg/L)", mortality: "Mortalité" },
  en: { task_type: "Work type", parcelle_name: "Plot", date: "Date", cost: "Cost (FCFA)", assignee: "Assigned to", equipment: "Equipment", pond_ref: "Pond", feed_type: "Feed", quantity_kg: "Quantity (kg)", name: "Name", category: "Category", phone: "Phone", city: "City", email: "Email", supplier_name: "Supplier", item: "Item", quantity: "Quantity", unit_cost: "Unit cost (FCFA)", target_name: "Pond", health_type: "Type", issue: "Issue", product: "Product", dosage: "Dosage", customer_name: "Customer", order_ref: "Order", avg_weight_g: "Avg weight (g)", oxygen_mg_l: "Oxygen (mg/L)", mortality: "Mortality" },
};

const FIELDS_BY_TYPE = {
  create_task: ["task_type", "parcelle_name", "date", "cost", "assignee", "equipment"],
  create_feeding: ["pond_ref", "feed_type", "quantity_kg", "cost"],
  create_supplier: ["name", "category", "phone", "city", "email"],
  create_customer: ["name", "phone", "city", "email"],
  create_purchase: ["supplier_name", "item", "quantity", "unit_cost"],
  create_health: ["target_name", "health_type", "issue", "product", "dosage"],
  send_reminder: ["customer_name", "order_ref"],
  log_measurement: ["pond_ref", "avg_weight_g", "oxygen_mg_l", "mortality"],
  complete_task: [],
};

const TITLE_KEY = {
  create_task: "ai_action_task",
  create_feeding: "ai_action_feeding",
  create_supplier: "ai_action_supplier",
  create_customer: "ai_action_customer",
  create_purchase: "ai_action_purchase",
  create_health: "ai_action_health",
  send_reminder: "ai_action_reminder",
  log_measurement: "ai_action_measure",
  complete_task: "ai_action_complete",
  generate_invoice: "ai_action_invoice",
};

// Récapitulatifs vocaux (commandes vocales directes)
const RECAP_TEXT = {
  fr: {
    create_task: (f) => `C'est fait : tâche ${f.task_type} planifiée sur ${f.parcelle_name}${f.date ? ` pour le ${f.date}` : ""}.`,
    create_feeding: (f) => `Alimentation enregistrée : ${f.quantity_kg} kg pour le bassin ${f.pond_ref}.`,
    create_supplier: (f) => `Fournisseur ${f.name} créé.`,
    create_customer: (f) => `Client ${f.name} créé.`,
    create_purchase: (f) => `Commande fournisseur enregistrée : ${f.quantity} ${f.item}${f.supplier_name ? ` chez ${f.supplier_name}` : ""}.`,
    create_health: (f) => `Traitement enregistré pour ${f.target_name}.`,
    send_reminder: (f) => `Relance envoyée à ${f.customer_name || "au client"} pour la commande ${f.order_ref}.`,
    complete_task: (f) => `Tâche ${f.task_label || ""} marquée terminée.`,
    log_measurement: (f) => `Relevé enregistré pour le bassin ${f.pond_ref}.`,
    generate_invoice: (f) => `Facture ${f.order_ref} ouverte.`,
  },
  en: {
    create_task: (f) => `Done: ${f.task_type} task scheduled on ${f.parcelle_name}${f.date ? ` for ${f.date}` : ""}.`,
    create_feeding: (f) => `Feeding logged: ${f.quantity_kg} kg for pond ${f.pond_ref}.`,
    create_supplier: (f) => `Supplier ${f.name} created.`,
    create_customer: (f) => `Customer ${f.name} created.`,
    create_purchase: (f) => `Purchase order logged: ${f.quantity} ${f.item}${f.supplier_name ? ` from ${f.supplier_name}` : ""}.`,
    create_health: (f) => `Treatment logged for ${f.target_name}.`,
    send_reminder: (f) => `Reminder sent to ${f.customer_name || "the customer"} for order ${f.order_ref}.`,
    complete_task: (f) => `Task ${f.task_label || ""} marked as done.`,
    log_measurement: (f) => `Measurement logged for pond ${f.pond_ref}.`,
    generate_invoice: (f) => `Invoice ${f.order_ref} opened.`,
  },
};

/** Exécute une action IA (confirmation carte OU commande vocale directe). Retourne { ok, recap } ou { ok:false, error }. */
export async function executeAction(action, fields, t, lang) {
  const R = RECAP_TEXT[lang === "en" ? "en" : "fr"];
  try {
    if (action.type === "create_task") {
      const plots = (await api.get("/parcelles")).data;
      const p = plots.find((x) => (x.name || "").toLowerCase() === (fields.parcelle_name || "").toLowerCase());
      await api.post("/tasks", {
        type: fields.task_type, parcelle_id: p?.id || "", parcelle_name: fields.parcelle_name,
        date: fields.date, cost: parseFloat(fields.cost) || 0,
        assignee: fields.assignee || "", equipment: fields.equipment || "", status: "planifié",
      });
      return { ok: true, recap: R.create_task(fields) };
    }
    if (action.type === "create_feeding") {
      const ponds = (await api.get("/ponds")).data;
      const ref = (fields.pond_ref || "").toLowerCase();
      const pond = ponds.find((x) => (x.name || "").toLowerCase() === ref || (x.ref || "").toLowerCase() === ref);
      if (!pond) return { ok: false, error: t("ai_action_pond_not_found") };
      await api.post("/pond_logs", {
        pond_id: pond.id, pond_name: pond.name, date: new Date().toISOString(),
        feed_kg: parseFloat(fields.quantity_kg) || 0, feed_cost: parseFloat(fields.cost) || 0,
        feed_type: fields.feed_type || "",
      });
      return { ok: true, recap: R.create_feeding(fields) };
    }
    if (action.type === "create_supplier") {
      await api.post("/suppliers", {
        name: fields.name, category: fields.category || "",
        phone: fields.phone || "", city: fields.city || "", email: fields.email || "",
      });
      return { ok: true, recap: R.create_supplier(fields) };
    }
    if (action.type === "create_customer") {
      await api.post("/customers", {
        name: fields.name, phone: fields.phone || "",
        city: fields.city || "", email: fields.email || "", type: fields.type || "particulier",
      });
      return { ok: true, recap: R.create_customer(fields) };
    }
    if (action.type === "create_purchase") {
      const suppliers = (await api.get("/suppliers")).data;
      const sname = (fields.supplier_name || "").toLowerCase();
      const sup = suppliers.find((x) => (x.name || "").toLowerCase() === sname);
      const qty = parseFloat(fields.quantity) || 0;
      const cost = parseFloat(fields.unit_cost) || 0;
      await api.post("/purchases", {
        supplier_id: sup?.id || "", supplier_name: sup?.name || fields.supplier_name || "",
        category: fields.category || "", item: fields.item,
        quantity: qty, unit_cost: cost, total: qty * cost, date: new Date().toISOString(),
      });
      return { ok: true, recap: R.create_purchase(fields) };
    }
    if (action.type === "create_health") {
      const ponds = (await api.get("/ponds")).data;
      const tname = (fields.target_name || "").toLowerCase();
      const pond = ponds.find((x) => (x.name || "").toLowerCase() === tname);
      await api.post("/health_records", {
        type: fields.health_type || "traitement", target_id: pond?.id || "",
        target_name: fields.target_name, domain: "pisciculture",
        issue: fields.issue || "", product: fields.product || "", dosage: fields.dosage || "",
        operator: "", date: new Date().toISOString(),
      });
      return { ok: true, recap: R.create_health(fields) };
    }
    if (action.type === "send_reminder") {
      await api.post("/ai/remind", { order_ref: fields.order_ref });
      return { ok: true, recap: R.send_reminder(fields) };
    }
    if (action.type === "complete_task") {
      await api.put(`/tasks/${action.task_id}`, { status: "terminé" });
      return { ok: true, recap: R.complete_task(fields) };
    }
    if (action.type === "log_measurement") {
      const ponds = (await api.get("/ponds")).data;
      const ref = (fields.pond_ref || "").toLowerCase();
      const pond = ponds.find((x) => (x.name || "").toLowerCase() === ref || (x.ref || "").toLowerCase() === ref);
      if (!pond) return { ok: false, error: t("ai_action_pond_not_found") };
      await api.post("/pond_logs", {
        pond_id: pond.id, pond_name: pond.name, date: new Date().toISOString(),
        avg_weight_g: parseFloat(fields.avg_weight_g) || 0,
        oxygen_mg_l: fields.oxygen_mg_l !== "" && fields.oxygen_mg_l != null ? parseFloat(fields.oxygen_mg_l) : null,
        mortality: parseFloat(fields.mortality) || 0,
        feed_kg: 0, feed_cost: 0, note: "Correction proposée par l'assistant IA",
      });
      return { ok: true, recap: R.log_measurement(fields) };
    }
    if (action.type === "generate_invoice") {
      const orders = (await api.get("/orders")).data;
      const ref = (fields.order_ref || "").toLowerCase();
      const order = orders.find((o) => (o.ref || "").toLowerCase() === ref);
      if (!order) return { ok: false, error: t("ai_action_order_not_found") };
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/reports/invoice/${order.id}`, "_blank");
      return { ok: true, recap: R.generate_invoice(fields) };
    }
    return { ok: false, error: t("error") };
  } catch (err) {
    return { ok: false, error: formatApiErrorDetail(err.response?.data?.detail) };
  }
}

export default function AiActionCard({ action }) {
  const { t, lang } = useI18n();
  const L = FIELD_LABELS[lang === "en" ? "en" : "fr"];
  const [fields, setFields] = useState(() =>
    Object.fromEntries(Object.entries(action).filter(([k]) => k !== "type")));
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [cancelled, setCancelled] = useState(false);

  if (cancelled) return null;

  async function downloadInvoice() {
    setBusy(true);
    const r = await executeAction(action, fields, t, lang);
    if (r.ok) setDone(true);
    else toast.error(r.error);
    setBusy(false);
  }

  async function confirm() {
    setBusy(true);
    const r = await executeAction(action, fields, t, lang);
    if (r.ok) {
      setDone(true);
      toast.success(t("ai_action_saved"));
    } else {
      toast.error(r.error);
    }
    setBusy(false);
  }

  if (done) {
    return (
      <div className="flex items-center gap-1.5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-3 py-1.5 text-xs font-medium" data-testid="ai-action-done">
        <CheckCircle2 className="w-3.5 h-3.5" /> {t("ai_action_saved")}
      </div>
    );
  }

  if (action.type === "generate_invoice") {
    return (
      <button onClick={downloadInvoice} disabled={busy} data-testid="ai-action-invoice-btn"
        className="flex items-center gap-1.5 rounded-lg border border-primary/40 bg-primary/10 text-primary px-3 py-1.5 text-xs font-medium hover:bg-primary/20 transition-colors disabled:opacity-50">
        <Sparkles className="w-3.5 h-3.5" />
        {t("ai_action_invoice")}{fields.order_ref ? ` — ${fields.order_ref}` : ""}
      </button>
    );
  }

  return (
    <div className="rounded-xl border border-primary/40 bg-primary/5 p-3 space-y-2 min-w-[220px]" data-testid="ai-action-card">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-primary">
        <Sparkles className="w-3.5 h-3.5" /> {t(TITLE_KEY[action.type] || "ai_action_task")}
      </div>
      {(FIELDS_BY_TYPE[action.type] || []).map((f) => (
        <label key={f} className="block">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{L[f] || f}</span>
          <input
            className="w-full h-8 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
            type={f === "date" ? "date" : ["cost", "quantity_kg", "quantity", "unit_cost", "avg_weight_g", "oxygen_mg_l", "mortality"].includes(f) ? "number" : "text"}
            value={fields[f] ?? ""}
            onChange={(e) => setFields({ ...fields, [f]: e.target.value })}
            data-testid={`ai-action-field-${f}`}
          />
        </label>
      ))}
      <div className="flex gap-2 pt-1">
        <button onClick={confirm} disabled={busy} data-testid="ai-action-confirm"
          className="flex-1 h-8 rounded-md bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors disabled:opacity-50">
          {busy ? "…" : t("ai_action_confirm")}
        </button>
        <button onClick={() => setCancelled(true)} data-testid="ai-action-cancel"
          className="h-8 rounded-md border border-border px-3 text-xs text-muted-foreground hover:bg-secondary transition-colors">
          {t("cancel")}
        </button>
      </div>
    </div>
  );
}
